import type { TypographyOptions } from '@mui/material/styles/createTypography'

import '@fontsource-variable/inter'
import '@fontsource/monoton'

const standardFontFamily = [
  '"Inter Variable"',
  'Inter',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Helvetica',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
].join(', ')

/**
 * The Attendix wordmark. Monoton is a display face used for exactly one thing —
 * the logo — so it lives here as a named token rather than as a magic
 * `fontFamily="Monoton, cursive"` string repeated at each call site.
 */
export const wordmarkFontFamily = ['Monoton', 'cursive'].join(', ')

declare module '@mui/material/styles' {
  interface TypographyVariants {
    wordmark: React.CSSProperties
  }

  interface TypographyVariantsOptions {
    wordmark?: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    wordmark: true
  }
}

/**
 * The scale is built from the bottom up, for a dense operations tool that is
 * scanned rather than read: 14px table text is the base, and each step above it
 * exists because a real screen needs it — card header, stat value, page title.
 *
 * The previous scale topped out at 56px/48px for `h1`/`h2`, neither of which any
 * screen rendered; the largest heading actually used was 32px.
 */
export const typography: TypographyOptions = {
  fontFamily: standardFontFamily,
  button: {
    fontWeight: 600,
    letterSpacing: 0,
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.57,
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: 1.75,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.57,
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.5px',
    lineHeight: 2.5,
    textTransform: 'uppercase',
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.66,
  },
  // Reserved for a full-bleed heading; no screen uses it today.
  h1: {
    fontWeight: 700,
    fontSize: '2.5rem',
    lineHeight: 1.2,
    letterSpacing: '-0.022em',
  },
  h2: {
    fontWeight: 700,
    fontSize: '2rem',
    lineHeight: 1.22,
    letterSpacing: '-0.02em',
  },
  // Stat values on the dashboard.
  h3: {
    fontWeight: 700,
    fontSize: '1.75rem',
    lineHeight: 1.25,
    letterSpacing: '-0.018em',
  },
  // Screen titles ("Dashboard", "Course List").
  h4: {
    fontWeight: 700,
    fontSize: '1.5rem',
    lineHeight: 1.3,
    letterSpacing: '-0.015em',
  },
  h5: {
    fontWeight: 600,
    fontSize: '1.25rem',
    lineHeight: 1.35,
    letterSpacing: '-0.01em',
  },
  // Card headers.
  h6: {
    fontWeight: 600,
    fontSize: '1.0625rem',
    lineHeight: 1.4,
    letterSpacing: '-0.005em',
  },
  wordmark: {
    fontFamily: wordmarkFontFamily,
    fontWeight: 400,
    lineHeight: 1,
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  },
}
